/**
 * Chart PROVIDERS — the extensible model behind the chart-source selector.
 *
 * A provider is a named SET of one or more georeferenced charts that the user
 * swaps to as a whole (Vector ENC, RYA Raster, and later Imray / UKHO / NOAA).
 * Within a provider the app auto-selects the best chart for the current view by
 * coverage and scale, so a set of adjacent or multi-scale charts behaves as one
 * seamless source; a manual override lets the user pin a specific chart.
 *
 * This is deliberately data, not code: adding Imray/UKHO/NOAA later is a new
 * entry in CHART_PROVIDERS (vector or raster, one or many charts), with no
 * change to the renderer or the selector UI.
 *
 * A provider is inherently VECTOR or RASTER — that alone decides how its chart is
 * drawn. Satellite imagery is no longer a provider; it's an overlay (see
 * satelliteBasemap.ts and the satellite store) that sits under whichever provider
 * is active.
 */
import {
  maptilerVectorTiles,
  MAPTILER_VECTOR_MAXZOOM,
  MAPTILER_ATTRIBUTION,
} from './maptilerConfig';

export type ChartProviderKind = 'vector' | 'raster' | 'tiles';

/**
 * Where a raster chart's PMTiles archive lives, which decides how the loader
 * builds its URL:
 *   - 'bundled' (default): `path` is relative to the app base URL (shipped in the
 *     app or under public/).
 *   - 'remote': `path` is an absolute http(s) URL. The pmtiles protocol streams
 *     tiles from it with HTTP range requests (view-online, no download).
 *   - 'local': `path` is a device file URI from a downloaded pack, resolved for
 *     the WebView with Capacitor.convertFileSrc (offline).
 */
export type RasterSource = 'bundled' | 'remote' | 'local';

/** One georeferenced raster chart in a provider's set. */
export interface RasterChart {
  /** Stable id, e.g. 'rya-3'. */
  id: string;
  /** Short display name, e.g. 'Chart 3'. */
  name: string;
  /** Coverage in lon/lat: [minLon, minLat, maxLon, maxLat]. */
  bounds: [number, number, number, number];
  /** PMTiles path: relative (bundled), absolute URL (remote), or file URI (local). */
  path: string;
  /** Where the PMTiles lives; defaults to 'bundled' when absent. */
  source?: RasterSource;
  /** Tile pyramid zoom range stored in the PMTiles archive. */
  tileMinZoom: number;
  tileMaxZoom: number;
  /** The map zoom at which this chart sits at its natural scale (best-scale pick). */
  nativeZoom: number;
}

export interface ChartProvider {
  /** Stable id, e.g. 'vector' | 'rya-raster'. */
  id: string;
  /** Display name in the selector. */
  name: string;
  /** One-line character shown under the name. */
  short: string;
  kind: ChartProviderKind;
  /** The charts in the set. Empty for the vector provider (the app renders cells). */
  charts: RasterChart[];
  attribution?: string;
  /**
   * Static coverage bounds for the "available here" test, used when the set
   * renders cells and so carries no per-chart bounds (the vector training cells).
   * [minLon, minLat, maxLon, maxLat]. Providers with charts derive coverage from
   * those instead.
   */
  coverage?: [number, number, number, number];
  /**
   * For a 'tiles' provider: the XYZ raster base drawn AS the chart (an OSM-style
   * web map), with the seamark overlay composited on top. Absent for vector/raster
   * providers.
   */
  baseTiles?: { url: string; tileSize: number; maxzoom: number };
  /**
   * For a 'tiles' provider using a VECTOR base (OpenSeaMap): the OpenMapTiles v3
   * vector tile URL + native max zoom. A small light land/water/road base is
   * authored over it in map/vectorBasemap.ts. Preferred over baseTiles (raster).
   */
  baseVector?: { url: string; maxzoom: number };
  /**
   * When true, selecting this provider forces the OpenSeaMap seamark overlay on,
   * independently of the standalone overlay toggle (the OpenSeaMap provider is a
   * base + seamarks, so the marks are part of the product). See MapView's seamark
   * effect for how the two are combined.
   */
  impliesSeamarks?: boolean;
  /**
   * True for the fictional RYA training charts (the Namley practice area), false
   * for real-world sources. This is what the derived "training setup" notion keys
   * off: the TRAINING marker and the feedback tagging read this rather than a
   * separate mode flag, so picking a fictional chart is what marks practice water.
   */
  fictional?: boolean;
}

/**
 * The providers, in selector order. Vector is the existing S-57 cell rendering.
 * RYA Raster is the scanned paper charts; Chart 4 joins Chart 3 once its tiles
 * are built, and that is the only change needed to add it to the set.
 */
export const CHART_PROVIDERS: ChartProvider[] = [
  {
    id: 'vector',
    name: 'RYA vector',
    short: 'The S-57 cells we render',
    kind: 'vector',
    fictional: true,
    charts: [],
    // The fictional RYA Namley training area (same extent as the RYA raster set),
    // so "available here" is true when the sim boat sits in the training cells.
    coverage: [-6.4779, 45.5816, -5.5292, 46.528],
  },
  {
    // Real-world NOAA ENC cells (US), free public-domain S-57 run through the same
    // converter as the training cells and served from public/charts/noaa/. A vector
    // source for real-area use with the phone GPS. Cells render where they are (US),
    // separate in the world from the fictional training cells.
    id: 'noaa-enc',
    name: 'NOAA ENC (US)',
    short: 'Real US S-57 cells — phone GNSS',
    kind: 'vector',
    attribution: 'NOAA ENC® — public domain; training/testing use, not for navigation',
    charts: [],
  },
  {
    id: 'rya-raster',
    name: 'RYA Raster',
    short: 'Scanned paper charts',
    kind: 'raster',
    fictional: true,
    attribution: 'RYA training charts (fictitious area) — not for navigation',
    charts: [
      {
        id: 'rya-3',
        name: 'Chart 3',
        bounds: [-6.4779, 45.5816, -5.5292, 46.528],
        path: 'charts/raster/rya-chart-3.pmtiles',
        tileMinZoom: 6,
        tileMaxZoom: 14,
        nativeZoom: 11,
      },
      // Chart 4 is a montage of separate inset chartlets, each cut out and
      // georeferenced on its own graticule and added here as its own chart.
      {
        id: 'rya4-haire',
        name: 'Chart 4 — Haire Sound',
        bounds: [-6.0259, 46.1732, -5.7702, 46.3023],
        path: 'charts/raster/rya4-haire.pmtiles',
        tileMinZoom: 6,
        tileMaxZoom: 16,
        nativeZoom: 15,
      },
      {
        id: 'rya4-dawson',
        name: 'Chart 4 — Dawson Harbour',
        bounds: [-6.0051, 45.6965, -5.8959, 45.7886],
        path: 'charts/raster/rya4-dawson.pmtiles',
        tileMinZoom: 6,
        tileMaxZoom: 15,
        nativeZoom: 14,
      },
      {
        id: 'rya4-fitzroy',
        name: 'Chart 4 — Port Fitzroy',
        bounds: [-5.9309, 45.6136, -5.7788, 45.6865],
        path: 'charts/raster/rya4-fitzroy.pmtiles',
        tileMinZoom: 6,
        tileMaxZoom: 15,
        nativeZoom: 14,
      },
      {
        id: 'rya4-hamilton',
        name: 'Chart 4 — Hamilton Sound',
        bounds: [-6.2362, 45.6146, -6.1298, 45.6641],
        path: 'charts/raster/rya4-hamilton.pmtiles',
        tileMinZoom: 6,
        tileMaxZoom: 16,
        nativeZoom: 15,
      },
      {
        id: 'rya4-namley',
        name: 'Chart 4 — Namley Harbour',
        bounds: [-5.8195, 46.4076, -5.6692, 46.4694],
        path: 'charts/raster/rya4-namley.pmtiles',
        tileMinZoom: 6,
        tileMaxZoom: 16,
        nativeZoom: 15,
      },
      {
        id: 'rya4-fraser',
        name: 'Chart 4 — Port Fraser',
        bounds: [-6.0684, 46.3661, -5.9793, 46.4414],
        path: 'charts/raster/rya4-fraser.pmtiles',
        tileMinZoom: 6,
        tileMaxZoom: 16,
        nativeZoom: 15,
      },
    ],
  },
  {
    // Licensed BSB/KAP raster charts (e.g. Antares), converted to PMTiles by
    // scripts/convert_bsb.py. Its charts are NOT hardcoded: they load at runtime
    // from the generated manifest, so dropping new .kap files in and re-running
    // the converter is all it takes. Real-world charts — pair with Device GNSS.
    id: 'bsb',
    name: 'Antares charts',
    short: 'Licensed raster charts (.kap)',
    kind: 'raster',
    attribution: 'Licensed Antares charts (BSB/KAP) — local testing only, not redistributed',
    charts: [],
  },
  {
    // OpenSeaMap: a MapTiler OSM-style VECTOR base with the free OpenSeaMap seamark
    // layer (buoys, lights, beacons, harbours) composited on top. A 'tiles'
    // provider — the base IS the chart. Worldwide and online-only (live tiles, no
    // offline pack). Selecting it forces the seamark overlay on (impliesSeamarks).
    // The base is vector tiles (GPU-rendered, light on bandwidth), not raster PNGs.
    id: 'openseamap',
    name: 'OpenSeaMap',
    short: 'OSM base + live seamarks',
    kind: 'tiles',
    attribution: `${MAPTILER_ATTRIBUTION}, © OpenSeaMap contributors`,
    charts: [],
    // Global: usable anywhere there's a connection.
    coverage: [-180, -85, 180, 85],
    baseVector: { url: maptilerVectorTiles(), maxzoom: MAPTILER_VECTOR_MAXZOOM },
    impliesSeamarks: true,
  },
  // Satellite is no longer a provider. It's an overlay (SATELLITE_SOURCES in
  // satelliteBasemap.ts): turned on, it sits under a vector provider (blended,
  // photo base + chart detail on top) or replaces a raster provider entirely.
];

export function getProvider(id: string): ChartProvider {
  return CHART_PROVIDERS.find((p) => p.id === id) ?? CHART_PROVIDERS[0];
}

function centreOf(b: [number, number, number, number]): [number, number] {
  return [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2];
}
function contains(b: [number, number, number, number], c: [number, number]): boolean {
  return c[0] >= b[0] && c[0] <= b[2] && c[1] >= b[1] && c[1] <= b[3];
}
function area(b: [number, number, number, number]): number {
  return (b[2] - b[0]) * (b[3] - b[1]);
}

/** Do two lon/lat boxes overlap at all? Used to tell if a chart is on screen. */
export function boundsIntersect(
  a: [number, number, number, number],
  b: [number, number, number, number],
): boolean {
  return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}

/**
 * The union bounds covering ALL of a raster provider's charts (its whole
 * coverage), or null if the set is empty. Used to fly the map to where a provider
 * actually has charts when the current view is outside its coverage.
 */
export function providerCoverageBounds(
  charts: RasterChart[],
): [number, number, number, number] | null {
  if (charts.length === 0) return null;
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;
  for (const c of charts) {
    if (c.bounds[0] < minLon) minLon = c.bounds[0];
    if (c.bounds[1] < minLat) minLat = c.bounds[1];
    if (c.bounds[2] > maxLon) maxLon = c.bounds[2];
    if (c.bounds[3] > maxLat) maxLat = c.bounds[3];
  }
  return [minLon, minLat, maxLon, maxLat];
}

/**
 * The charts in a raster provider that overlap the given view box, nearest
 * centre first. Empty when the provider's charts are all elsewhere — which is
 * the signal the chart-source UI uses to say "no chart here, jump to one".
 */
export function chartsInView(
  provider: ChartProvider,
  view: [number, number, number, number],
): RasterChart[] {
  if (provider.charts.length === 0) return [];
  const vc: [number, number] = [(view[0] + view[2]) / 2, (view[1] + view[3]) / 2];
  return provider.charts
    .filter((c) => boundsIntersect(c.bounds, view))
    .sort((a, b) => {
      const ca = centreOf(a.bounds);
      const cb = centreOf(b.bounds);
      return Math.hypot(ca[0] - vc[0], ca[1] - vc[1]) - Math.hypot(cb[0] - vc[0], cb[1] - vc[1]);
    });
}

/**
 * The best chart in a raster provider for a given view centre and zoom. Charts
 * covering the centre win; among those we prefer one whose tile range suits the
 * zoom (so a finer-scale chart takes over as we zoom in), then the nearest
 * centre (so panning across a seam hands over to the chart we are more inside),
 * then the largest scale. Returns null for a vector or empty provider.
 */
export function bestChartFor(
  provider: ChartProvider,
  centre: [number, number],
  zoom: number,
): RasterChart | null {
  if (provider.kind !== 'raster' || provider.charts.length === 0) return null;
  const covering = provider.charts.filter((c) => contains(c.bounds, centre));
  const pool = covering.length ? covering : provider.charts;
  const dist = (c: RasterChart) => {
    const cc = centreOf(c.bounds);
    return Math.hypot(cc[0] - centre[0], cc[1] - centre[1]);
  };
  const sorted = [...pool].sort((a, b) => {
    // Prefer the chart whose natural scale suits the current zoom, so zooming in
    // hands over from a small-scale area chart to a larger-scale harbour plan,
    // and zooming out falls back to the broad chart.
    const za = Math.abs(a.nativeZoom - zoom);
    const zb = Math.abs(b.nativeZoom - zoom);
    if (Math.abs(za - zb) > 0.001) return za - zb;
    const dd = dist(a) - dist(b);
    if (Math.abs(dd) > 1e-9) return dd;
    return area(a.bounds) - area(b.bounds);
  });
  return sorted[0] ?? null;
}

/**
 * How many charts we lay down at once. A quilt of a dozen is plenty for a screen
 * (one wide chart plus the plans over it) and keeps the number of live PMTiles
 * sources sane on a phone.
 */
export const MAX_QUILT_CHARTS = 12;

/**
 * The charts to draw as a QUILT for this view, ordered bottom first (coarsest
 * underneath, the most detailed plan on top).
 *
 * A provider is a set, and the Antares set is 823 harbour plans plus a handful of
 * wider charts. Picking one chart meant everything outside it drew no chart at all.
 * So we take every chart that touches the view AND whose tile pyramid actually
 * reaches this zoom (a plan whose tiles start at z12 renders nothing at z11, so
 * carrying it would only cost us a source), and stack them by natural scale.
 *
 * Where two charts overlap, the finer one wins by sitting on top, which is the same
 * rule the vector cells follow. Ties go to the larger chart underneath, so a big
 * area chart never covers the small plan sitting inside it.
 */
export function quiltCharts(
  provider: ChartProvider,
  view: [number, number, number, number],
  zoom: number,
): RasterChart[] {
  if (provider.kind !== 'raster' || provider.charts.length === 0) return [];
  const vc: [number, number] = [(view[0] + view[2]) / 2, (view[1] + view[3]) / 2];
  const candidates = provider.charts.filter(
    (c) => boundsIntersect(c.bounds, view) && zoom >= c.tileMinZoom - 0.001,
  );
  // Over the cap, keep the charts that actually fill the screen: the wide chart
  // under us and whatever plan we are over. What drops out is the specks round the
  // edge, which would be a few pixels each anyway. Distance from the view centre
  // breaks the tie so the ones we are most inside survive.
  const kept =
    candidates.length <= MAX_QUILT_CHARTS
      ? candidates
      : [...candidates]
          .sort((a, b) => {
            const cov = viewCoverage(b.bounds, view) - viewCoverage(a.bounds, view);
            if (Math.abs(cov) > 1e-9) return cov;
            const ca = centreOf(a.bounds);
            const cb = centreOf(b.bounds);
            return (
              Math.hypot(ca[0] - vc[0], ca[1] - vc[1]) - Math.hypot(cb[0] - vc[0], cb[1] - vc[1])
            );
          })
          .slice(0, MAX_QUILT_CHARTS);
  return [...kept].sort((a, b) => {
    const nz = a.nativeZoom - b.nativeZoom;
    if (Math.abs(nz) > 0.001) return nz;
    return area(b.bounds) - area(a.bounds);
  });
}

/** Fraction of the view box (0..1) that a chart's bounds cover. */
export function viewCoverage(
  chartBounds: [number, number, number, number],
  view: [number, number, number, number],
): number {
  const va = area(view);
  if (va <= 0) return 0;
  const w = Math.max(0, Math.min(chartBounds[2], view[2]) - Math.max(chartBounds[0], view[0]));
  const h = Math.max(0, Math.min(chartBounds[3], view[3]) - Math.max(chartBounds[1], view[1]));
  return Math.min(1, (w * h) / va);
}

// Hysteresis thresholds for the sticky auto-pick. Tuned so a detailed harbour
// chart stays put while you scroll around or zoom out a bit, and only hands off
// when you've genuinely left it or zoomed below where its tiles exist.
/** Zoom past a finer chart's min before auto-upgrading to it (so zooming IN
 *  still hands up to the detailed chart, but not the instant it becomes legal). */
const UPGRADE_ZOOM_MARGIN = 0.5;
/** Keep the current chart while it still covers at least this much of the view. */
const KEEP_COVER_FRAC = 0.2;

/**
 * Sticky auto-pick with hysteresis. Prefers KEEPING the currently-displayed
 * chart while it still renders at this zoom (its tiles exist) and still
 * reasonably covers the view (centre inside it, or it covers >= KEEP_COVER_FRAC
 * of the viewport), so a small pan or zoom-out doesn't snap to a smaller-scale
 * chart. It still upgrades to a finer-scale chart that covers the centre once
 * you've zoomed into that chart's range (by UPGRADE_ZOOM_MARGIN). When there's
 * no current chart, or the current one no longer applies, it falls back to the
 * fresh `bestChartFor`. The differing keep vs upgrade thresholds give margin so
 * it doesn't oscillate at a boundary.
 */
export function pickRasterChart(
  provider: ChartProvider,
  currentId: string | null,
  view: [number, number, number, number],
  centre: [number, number],
  zoom: number,
): RasterChart | null {
  if (provider.kind !== 'raster' || provider.charts.length === 0) return null;
  const fresh = bestChartFor(provider, centre, zoom);
  const cur = currentId ? (provider.charts.find((c) => c.id === currentId) ?? null) : null;
  if (!cur || !fresh) return cur ?? fresh;
  if (fresh.id === cur.id) return cur;

  // Hand UP to a finer-scale chart that covers the centre once we're comfortably
  // inside its useful zoom range — so zooming in reaches the detailed chart.
  const finerUpgrade =
    fresh.nativeZoom > cur.nativeZoom &&
    contains(fresh.bounds, centre) &&
    zoom >= fresh.tileMinZoom + UPGRADE_ZOOM_MARGIN;
  if (finerUpgrade) return fresh;

  // Otherwise hold the current chart while its tiles still render here and it
  // still reasonably covers the view (sticky against pan and zoom-out).
  const stillRenders = zoom >= cur.tileMinZoom - 0.001;
  const stillCovers =
    contains(cur.bounds, centre) || viewCoverage(cur.bounds, view) >= KEEP_COVER_FRAC;
  if (stillRenders && stillCovers) return cur;
  return fresh;
}

/** Path (relative to the app base URL) of the generated BSB chart manifest. */
export const BSB_MANIFEST_PATH = 'charts/raster/bsb/manifest.json';

/**
 * Download-on-demand chart packs. A pack is a named region of the Antares set,
 * hosted remotely (e.g. as GitHub Release assets). The packs manifest lists what
 * is available; each pack points at its own per-chart manifest (same shape as the
 * bundled BSB manifest) and a base URL its PMTiles files sit under.
 */
export interface ChartPack {
  /** Stable id / slug, e.g. 'sound-of-mull'. */
  id: string;
  /** Human region name shown in the store, e.g. 'Sound of Mull & Tobermory'. */
  region: string;
  /** Coverage union of the pack's charts: [minLon, minLat, maxLon, maxLat]. */
  bounds: [number, number, number, number];
  /** Total download size of the pack's PMTiles, bytes. */
  sizeBytes: number;
  /** Number of charts in the pack. */
  chartCount: number;
  /** Absolute URL of the pack's per-chart manifest (JSON: { charts: RasterChart[] }). */
  chartsUrl: string;
  /** Absolute base URL each chart file name resolves against (trailing slash). */
  baseUrl: string;

  /**
   * What the pack contains. Raster packs are PMTiles streamed by range request;
   * vector packs are S-57 cells fetched whole and loaded on demand. Absent means
   * raster, so packs published before this existed still read correctly.
   */
  kind?: 'raster' | 'vector';
  /** Vector packs: absolute URL of the cell manifest (JSON: { cells: CellMeta[] }). */
  cellsUrl?: string;
  /** Vector packs: how many cells, alongside chartCount. */
  cellCount?: number;
  /** Latest issue date across the pack's charts or cells, YYYYMMDD. */
  issued?: string;
}

/** True for a pack of ENC cells rather than raster tiles. */
export function isVectorPack(pack: ChartPack): boolean {
  return pack.kind === 'vector';
}

export interface ChartPacksManifest {
  version: number;
  packs: ChartPack[];
}

/**
 * Path (relative to the app base URL) of the NOAA cells the app actually ships.
 *
 * Not manifest.json. The ingest writes that one to describe everything it
 * converted, which after a full run is all 7,133 NOAA cells, and it is the input
 * to build-enc-packs.mjs rather than something the app can reach. Pointing the
 * app at it meant residency asking for cells that were not in the build and
 * collecting a 404 for each, and a chart list offering seven thousand entries
 * that could not be opened.
 *
 * bundled.json lists only the committed cells, written by
 * scripts/build-bundled-manifest.mjs. Everything else arrives as a pack.
 */
export const NOAA_MANIFEST_PATH = 'charts/noaa/bundled.json';

/**
 * A provider with any runtime-loaded charts merged in. The BSB provider is
 * populated from the manifest the converter writes, so its charts arrive at
 * runtime; every other provider is static and returned unchanged.
 */
export function resolveProvider(
  id: string,
  bsbCharts: RasterChart[],
  noaaCharts: RasterChart[] = [],
  packCharts: RasterChart[] = [],
): ChartProvider {
  const p = getProvider(id);
  // The Antares (bsb) provider merges the bundled charts with any download-on-
  // demand pack charts the user has switched on (viewed online or downloaded),
  // de-duplicated by id so a downloaded pack supersedes the same chart online.
  if (p.id === 'bsb') return { ...p, charts: mergeById(bsbCharts, packCharts) };
  if (p.id === 'noaa-enc') return { ...p, charts: noaaCharts };
  return p;
}

/** Merge chart lists, later entries winning on a clashing id. */
function mergeById(...lists: RasterChart[][]): RasterChart[] {
  const byId = new Map<string, RasterChart>();
  for (const list of lists) for (const c of list) byId.set(c.id, c);
  return [...byId.values()];
}

/**
 * A coarse region label from an Antares-style chart name, used to group a big
 * chart set. "AC 5831ND Talmine Bay, Kyle of Tongue, edition 2" -> "Kyle of
 * Tongue" (the last comma-segment that isn't the edition note).
 */
export function chartRegion(name: string): string {
  const segs = name
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s && !/^edition\b/i.test(s));
  return segs.length > 1 ? segs[segs.length - 1] : 'Other charts';
}

/** The n charts nearest a view centre, for a default jump list when none are in view. */
export function nearestCharts(
  provider: ChartProvider,
  centre: [number, number],
  n: number,
): RasterChart[] {
  if (provider.charts.length === 0) return [];
  return [...provider.charts]
    .sort((a, b) => {
      const ca = centreOf(a.bounds);
      const cb = centreOf(b.bounds);
      return Math.hypot(ca[0] - centre[0], ca[1] - centre[1]) - Math.hypot(cb[0] - centre[0], cb[1] - centre[1]);
    })
    .slice(0, n);
}

/**
 * A provider's coverage box for the "available here" test. Providers with charts
 * derive it from the union of their chart bounds; a cell-rendering provider
 * (empty charts) uses its static `coverage`. Null when neither is known.
 */
export function providerCoverage(p: ChartProvider): [number, number, number, number] | null {
  if (p.charts.length) {
    let minLon = Infinity;
    let minLat = Infinity;
    let maxLon = -Infinity;
    let maxLat = -Infinity;
    for (const c of p.charts) {
      minLon = Math.min(minLon, c.bounds[0]);
      minLat = Math.min(minLat, c.bounds[1]);
      maxLon = Math.max(maxLon, c.bounds[2]);
      maxLat = Math.max(maxLat, c.bounds[3]);
    }
    return [minLon, minLat, maxLon, maxLat];
  }
  return p.coverage ?? null;
}

/**
 * Whether a provider's coverage includes the given position (lon/lat), i.e. it's
 * usable "here". False when we have no position or no coverage — the picker then
 * shows everything under one list rather than guessing.
 */
export function isProviderAvailableAt(
  p: ChartProvider,
  pos: [number, number] | null,
): boolean {
  if (!pos) return false;
  const b = providerCoverage(p);
  if (!b) return false;
  return pos[0] >= b[0] && pos[0] <= b[2] && pos[1] >= b[1] && pos[1] <= b[3];
}

/**
 * Chart sources in the "store" (not installed/subscribed yet). This is REAL UI
 * data so the More-charts tab is ready to wire to a real backend later, but the
 * install/subscribe action is STUBBED in the picker (no backend exists), so
 * tapping it just reports "not yet available". See ChartProviderPicker.
 */
export interface StoreChart {
  id: string;
  name: string;
  short: string;
  kind: ChartProviderKind;
  /** Whether it's a one-off install or an ongoing subscription (labels the button). */
  action: 'install' | 'subscribe';
}

export const STORE_CHARTS: StoreChart[] = [
  { id: 'ukho-admiralty', name: 'Admiralty (UKHO)', short: 'Official ENC, worldwide', kind: 'vector', action: 'subscribe' },
  { id: 'imray', name: 'Imray', short: 'Leisure charts, UK & Med', kind: 'raster', action: 'subscribe' },
  { id: 'navionics', name: 'Navionics', short: 'Popular leisure vector charts', kind: 'vector', action: 'subscribe' },
];


/**
 * The fictional RYA training charts (the Namley practice area) vs real-world
 * sources. A provider is fictional when it carries the flag; everything else is
 * real water. Unknown ids fall back to the first provider (vector), which is
 * fictional, so an unrecognised id errs on the safe side of being marked training.
 */
export function isFictionalProvider(providerId: string): boolean {
  return getProvider(providerId).fictional === true;
}

/**
 * Whether the RYA training tide model has anything to say about the water we are
 * looking at. It does not, unless the charts are the fictional training set.
 *
 * This is a safety guard, not a tidy-up. The training model is Namley, a made-up
 * place, and its events all sit in a 2019 window that the training scrubber never
 * leaves. So without this check a boat on a real NOAA chart in Puerto Rico reads a
 * real, plausible-looking height of tide off a fictional port and adds it to a real
 * charted depth. That number reaches the depth-under-keel alarm and the bridge
 * clearance readout, which is exactly where an invented figure must never turn up.
 * Callers should treat false as "no height of tide", which every consumer already
 * handles as chart datum, the cautious reading.
 */
/**
 * Whether real forecast weather means anything on this chart. It does wherever the
 * chart shows real water, which is every provider that is not the fictional
 * training set. This used to be hardcoded to the Antares 'bsb' provider, which
 * quietly left the real NOAA cells sitting in a world with synthetic wind.
 */
export function providerHasRealWeather(providerId: string): boolean {
  return !isFictionalProvider(providerId);
}

export function trainingTideCovers(providerId: string): boolean {
  return isFictionalProvider(providerId);
}

/**
 * The derived "this is a training setup" notion that stands in for the old
 * world-mode flag. It's a training setup when the selected charts are the
 * fictional RYA set OR the position comes from the simulator, because either one
 * on its own means the screen is not a real boat on real water and so must be
 * marked as practice, not for navigation.
 */
export function isTrainingSetup(
  providerId: string,
  sourceKind: 'sim' | 'device',
): boolean {
  return isFictionalProvider(providerId) || sourceKind === 'sim';
}
